// HUDManager.js
// Responsible for managing HUD elements: fuel, speed, coins, laser meter

export default class HUDManager {
    constructor() {
        this.fuelBar = null;
        this.fuelText = null;
        this.outOfFuelWarning = null;
        this.fuelFlashInterval = null;
        this.speedDisplay = null;
        this.speedDisplayComponent = null;
        this.coinsDisplay = null;
        // Laser meter elements
        this.laserBar = null;
        this.laserText = null;
        this.laserRechargeInterval = null;
        this.laserCharge = 100; // Starting laser charge (%)
        this.laserRechargeRate = 10; // % per second
        this.laserIsRecharging = false;
    }

    // Check if laser is available for use
    isLaserAvailable() {
        return this.laserCharge > 0;
    }

    // Deplete laser when used
    depleteLaser(amount = 25) {
        // Stop any existing recharge interval
        this.stopLaserRecharge();

        // Calculate new charge level
        const newCharge = Math.max(0, this.laserCharge - amount);

        // Update the meter
        this.updateLaserMeter(newCharge);

        // If depleted, show notification (optional)
        // if (newCharge === 0) {
        //     this.showNotificationMessage("Laser depleted! Recharging...", "rgba(255, 0, 0, 0.8)");
        // }

        // Start recharging if not at 100%
        if (newCharge < 100) {
            this.startLaserRecharge();
        }

        return newCharge > 0; // Return whether laser can still be used
    }

    // Start laser recharge process
    startLaserRecharge() {
        if (this.laserRechargeInterval) return; // Already recharging

        this.laserIsRecharging = true;

        // Create recharge interval (update every 100ms)
        this.laserRechargeInterval = setInterval(() => {
            // Calculate recharge amount (rechargeRate per second, divided by 10 for 100ms interval)
            const rechargeAmount = this.laserRechargeRate / 10;

            // Update charge
            this.laserCharge = Math.min(100, this.laserCharge + rechargeAmount);
            this.updateLaserMeter();

            // If fully recharged, stop the interval
            if (this.laserCharge >= 100) {
                this.stopLaserRecharge();
                // this.showNotificationMessage("Laser fully charged!", "rgba(0, 255, 0, 0.8)");
            }
        }, 100);
    }

    // Stop laser recharge process
    stopLaserRecharge() {
        if (this.laserRechargeInterval) {
            clearInterval(this.laserRechargeInterval);
            this.laserRechargeInterval = null;
            this.laserIsRecharging = false;
        }
    }

    initialize() {
        this.fuelBar = document.getElementById('fuel-level');
        this.fuelText = document.getElementById('fuel-percentage');

        this.coinsDisplay = document.getElementById('coins-value');
        this.createSpeedDisplay();
        this.createFuelDisplay();
        this.createLaserMeter();
    }

    createSpeedDisplay() {
        if (this.speedDisplayComponent) return this.speedDisplayComponent;
        if (this._speedDisplayPromise) return this._speedDisplayPromise;
        this._speedDisplayPromise = import('../components/hud/SpeedDisplay.js').then(module => {
            const SpeedDisplay = module.default;
            this.speedDisplayComponent = new SpeedDisplay({
                position: {
                    top: window.innerWidth < 768 ? '10px' : '20px',
                    left: window.innerWidth < 768 ? '10px' : '20px'
                }
            });
            this.speedDisplayComponent.initialize();
            return this.speedDisplayComponent;
        });
        return this._speedDisplayPromise;
    }

    createFuelDisplay() {
        // Create fuel display if it doesn't exist
        if (!this.fuelBar) {
            // Create fuel container
            const fuelContainer = document.createElement('div');
            fuelContainer.id = 'fuel-container';
            fuelContainer.style.position = 'fixed';
            fuelContainer.style.top = window.innerWidth < 768 ? '10px' : '20px';
            fuelContainer.style.right = window.innerWidth < 768 ? '10px' : '20px';
            fuelContainer.style.width = window.innerWidth < 768 ? '50px' : '60px';
            fuelContainer.style.textAlign = 'center';
            fuelContainer.style.color = '#F5E6D0';
            fuelContainer.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            fuelContainer.style.padding = window.innerWidth < 768 ? '8px' : '10px';
            fuelContainer.style.borderRadius = '6px';

            // Create fuel label
            const fuelLabel = document.createElement('div');
            fuelLabel.textContent = 'FUEL';
            fuelLabel.style.fontSize = window.innerWidth < 768 ? '10px' : '11px';
            fuelLabel.style.fontWeight = 'bold';
            fuelLabel.style.marginBottom = '5px';

            // Create fuel gauge background
            const fuelBarContainer = document.createElement('div');
            fuelBarContainer.style.width = window.innerWidth < 768 ? '25px' : '30px';
            fuelBarContainer.style.height = window.innerWidth < 768 ? '100px' : '120px';
            fuelBarContainer.style.background = 'rgba(255,255,255,0.08)';
            fuelBarContainer.style.borderRadius = '6px';
            fuelBarContainer.style.overflow = 'hidden';
            fuelBarContainer.style.border = '1px solid rgba(245, 230, 208, 0.15)';
            fuelBarContainer.style.margin = '0 auto';
            fuelBarContainer.style.position = 'relative';

            // Create fuel gauge fill
            const fuelBar = document.createElement('div');
            fuelBar.id = 'fuel-level';
            fuelBar.style.width = '100%';
            fuelBar.style.height = '100%';
            fuelBar.style.backgroundColor = '#7BC67E';
            fuelBar.style.position = 'absolute';
            fuelBar.style.bottom = '0';
            fuelBar.style.left = '0';
            fuelBar.style.transition = 'height 0.3s, background-color 0.3s';

            // Create fuel percentage text
            const fuelText = document.createElement('div');
            fuelText.id = 'fuel-percentage';
            fuelText.textContent = '100%';
            fuelText.style.marginTop = '5px';
            fuelText.style.fontSize = window.innerWidth < 768 ? '12px' : '14px';

            // Assemble fuel gauge components
            fuelBarContainer.appendChild(fuelBar);
            fuelContainer.appendChild(fuelLabel);
            fuelContainer.appendChild(fuelBarContainer);
            fuelContainer.appendChild(fuelText);

            document.body.appendChild(fuelContainer);

            // Store references
            this.fuelBar = fuelBar;
            this.fuelText = fuelText;
        }

        return this.fuelBar;
    }

    createLaserMeter() {
        // Remove existing laser container if present
        const existing = document.getElementById('laser-container');
        if (existing) {
            existing.parentNode.removeChild(existing);
            this.laserBar = null;
            this.laserText = null;
        }
        // Create laser meter if it doesn't exist
        if (!this.laserBar) {
            // Create laser container
            const laserContainer = document.createElement('div');
            laserContainer.id = 'laser-container';
            laserContainer.style.position = 'fixed';
            laserContainer.style.top = window.innerWidth < 768 ? '10px' : '20px';
            laserContainer.style.right = window.innerWidth < 768 ? '70px' : '110px';
            laserContainer.style.width = window.innerWidth < 768 ? '50px' : '60px';
            laserContainer.style.textAlign = 'center';
            laserContainer.style.color = '#F5E6D0';
            laserContainer.style.backgroundColor = 'rgba(20, 16, 10, 0.55)';
            laserContainer.style.padding = window.innerWidth < 768 ? '8px' : '10px';
            laserContainer.style.borderRadius = '6px';

            // Create laser label
            const laserLabel = document.createElement('div');
            laserLabel.textContent = 'LASER';
            laserLabel.style.fontSize = window.innerWidth < 768 ? '10px' : '11px';
            laserLabel.style.fontWeight = 'bold';
            laserLabel.style.marginBottom = '5px';

            // Create laser gauge background
            const laserBarContainer = document.createElement('div');
            laserBarContainer.style.width = window.innerWidth < 768 ? '25px' : '30px';
            laserBarContainer.style.height = window.innerWidth < 768 ? '100px' : '120px';
            laserBarContainer.style.background = 'rgba(255,255,255,0.08)';
            laserBarContainer.style.borderRadius = '6px';
            laserBarContainer.style.overflow = 'hidden';
            laserBarContainer.style.border = '1px solid rgba(245, 230, 208, 0.15)';
            laserBarContainer.style.margin = '0 auto';
            laserBarContainer.style.position = 'relative';

            // Create laser gauge fill
            const laserBar = document.createElement('div');
            laserBar.id = 'laser-level';
            laserBar.style.width = '100%';
            laserBar.style.height = '100%';
            laserBar.style.backgroundColor = '#C45B8A';
            laserBar.style.position = 'absolute';
            laserBar.style.bottom = '0';
            laserBar.style.left = '0';
            laserBar.style.transition = 'height 0.3s, background-color 0.3s';

            // Create laser percentage text
            const laserText = document.createElement('div');
            laserText.id = 'laser-percentage';
            laserText.textContent = '100%';
            laserText.style.marginTop = '5px';
            laserText.style.fontSize = window.innerWidth < 768 ? '12px' : '14px';

            // Assemble laser gauge components
            laserBarContainer.appendChild(laserBar);
            laserContainer.appendChild(laserLabel);
            laserContainer.appendChild(laserBarContainer);
            laserContainer.appendChild(laserText);

            document.body.appendChild(laserContainer);

            // Store references
            this.laserBar = laserBar;
            this.laserText = laserText;
        }

        return this.laserBar;
    }

    updateFuel(value) {
        if (this.fuelBar) {
            this.fuelBar.style.height = `${value}%`;
        }
        if (this.fuelText) {
            this.fuelText.textContent = `${value}%`;
        }
    }

    updateCoins(value) {
        if (this.coinsDisplay) {
            this.coinsDisplay.textContent = value;
        }
    }

    updateSpeed(value) {
        if (this.speedDisplayComponent) {
            // value is in m/s, convert to km/h for display
            this.speedDisplayComponent.update(value * 3.6, 'km/h');
        }
    }

    updateLaserMeter(percentage = null) {
        if (!this.laserBar || !this.laserText) {
            this.createLaserMeter();
        }
        // If a percentage is provided, update the charge level
        if (percentage !== null) {
            this.laserCharge = Math.max(0, Math.min(100, percentage));
        }
        // Update laser percentage text
        this.laserText.textContent = `${Math.round(this.laserCharge)}%`;
        // Update laser bar height for vertical gauge (matching fuel gauge)
        this.laserBar.style.height = `${this.laserCharge}%`;
        // Change laser bar color based on charge level
        if (this.laserCharge <= 10) {
            // Critical - muted red
            this.laserBar.style.backgroundColor = '#D45B4A';
        } else if (this.laserCharge <= 30) {
            // Low - muted orange
            this.laserBar.style.backgroundColor = '#D4924B';
        } else {
            // Good - muted pink for laser
            this.laserBar.style.backgroundColor = '#C45B8A';
        }
        return this.laserCharge;
    }

    // ... Add other HUD methods as needed
}
